import React, { type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import {
  EmptyState,
  EmptyStateArtwork,
  EmptyStateTitle,
  EmptyStateDescription,
} from '@instacart/ids-customers'
import { spacing } from '@retailer-platform/shared-components'
import { Alert } from '@retailer-platform/shared-components/src/tds'
import { usePartnerContext } from '@retailer-platform/dashboard/utils'
import reportsEmptyStateImg from '../../assets/reports-empty-state-illustration.png'
import { useDomainMessages } from '../../utils/domain/intl'
import { ReportsNormalizedReportsGridList } from './components/grid-list/ReportsNormalizedReportsGridList'
import { ReportsNormalizedReportsListFilters } from './components/filters/ReportsNormalizedReportsListFilters'
import { useReportsNormalizedReportsList } from './utils/useReportsNormalizedReportsList.hooks'
import { type ReportTypeConfig } from './utils/ReportsNormalizedReportsList.utils'

interface ReportsNormalizedReportsListProps {
  partnerId: string
  retailerId: string
  reportTypesConfig: ReportTypeConfig
  filterUnavailableReportTypes?: boolean
}

const StyledGridListWrapper = styled.div({
  padding: `0 ${spacing.X24}`,
  overflow: 'hidden',
  width: '100%',
  flex: 1,
})

const StyledAlertWrapper = styled.div({
  padding: spacing.X24,
})

export const InstacartManagedReportsList: FunctionComponent<
  React.PropsWithChildren<ReportsNormalizedReportsListProps>
> = ({ partnerId, retailerId, reportTypesConfig, filterUnavailableReportTypes }) => {
  const {
    apiResult,
    loading,
    refetch,
    canLoadMore,
    loadMore,
    dateRangePreset,
    setDateRangePreset,
    dateRange,
    setDateRange,
    reportType,
    setReportType,
    error,
    warehouseOptions,
    retailerIds,
    setRetailerIds,
    storeGroupId,
    setStoreGroupId,
    isHeavyQuery,
    hasFullPartnerAccess,
  } = useReportsNormalizedReportsList({
    partnerId,
    retailerId,
    reportTypesConfig,
  })
  const { warehouses } = usePartnerContext()
  const messages = useDomainMessages({
    heavyQueryTitle: 'reportsDomain.instacartManagedReports.heavyQueryPrompt.title',
    heavyQueryDescription: 'reportsDomain.instacartManagedReports.heavyQueryPrompt.description',
    fullPartnerAccessRequired: 'reportsDomain.instacartManagedReports.fullPartnerAccessRequired',
  })
  // @ts-expect-error TS2339 — strictNullChecks. TODO: fix strict null check
  const hasPgpReport = apiResult?.some(report => report.fileType === 'pgp') ?? false

  const showHeavyQueryPrompt = isHeavyQuery && !loading
  const showFullPartnerAccessRequired =
    retailerIds.length === 0 &&
    !hasFullPartnerAccess &&
    // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
    warehouses.length > 1

  return (
    <React.Fragment>
      <ReportsNormalizedReportsListFilters
        reportCount={apiResult?.length ?? 0}
        partnerId={partnerId}
        retailerId={retailerId}
        dateRangePreset={dateRangePreset}
        setDateRangePreset={setDateRangePreset}
        // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
        dateRange={dateRange}
        setDateRange={setDateRange}
        // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
        reportType={reportType}
        setReportType={setReportType}
        warehouseOptions={warehouseOptions}
        retailerIds={retailerIds}
        setRetailerIds={setRetailerIds}
        storeGroupId={storeGroupId}
        setStoreGroupId={setStoreGroupId}
        reportTypesConfig={reportTypesConfig}
        filterUnavailableReportTypes={filterUnavailableReportTypes}
        hasPgpReport={hasPgpReport}
      />
      {showFullPartnerAccessRequired && (
        <StyledAlertWrapper>
          <Alert variant="warning">{messages.fullPartnerAccessRequired}</Alert>
        </StyledAlertWrapper>
      )}
      <StyledGridListWrapper>
        {showHeavyQueryPrompt ? (
          <EmptyState>
            <EmptyStateArtwork src={reportsEmptyStateImg} />
            <EmptyStateTitle>{messages.heavyQueryTitle}</EmptyStateTitle>
            <EmptyStateDescription>{messages.heavyQueryDescription}</EmptyStateDescription>
          </EmptyState>
        ) : (
          <ReportsNormalizedReportsGridList
            // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
            reports={apiResult}
            loading={loading}
            loadMore={loadMore}
            canLoadMore={canLoadMore}
            refetch={refetch}
            error={error}
            isHeavyQuery={isHeavyQuery}
            reportType={reportType}
          />
        )}
      </StyledGridListWrapper>
    </React.Fragment>
  )
}
